import { useTranslate, useMany, type CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Form, Row, Col, Input, Rate } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ReviewOut, PublicationListOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { NullFilter } from '@/components/common/NullFilter';
import { PublicationSelectFilter } from '@/components/user_catalog/publications/PublicationSelectFilter';
import { ReviewStatusTag } from '@/components/feedback/tags';
import { ReviewStatusFilter } from '@/components/feedback/ReviewStatusFilter';
import { isFieldFiltered } from '@/utils/filters';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const ReviewList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<ReviewOut>({
      pagination: { pageSize: 20 },
      resource: 'feedback.reviews',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const publicationIds = [
    ...new Set(
      (tableProps.dataSource ?? []).map((r) => r.publication_id).filter(Boolean)
    ),
  ];

  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });

  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p) => [p.id, p])
  );

  return (
    <List canCreate={false}>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('feedback.reviews.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table {...rowNav('feedback.reviews', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="publication_id"
          title={t('feedback.reviews.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publication_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublicationSelectFilter
              field="publication_id"
              placeholder={t('feedback.reviews.filter.publication_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="author_name"
          title={t('feedback.reviews.fields.author_name')}
        />

        <Table.Column
          dataIndex="rating"
          title={t('feedback.reviews.fields.rating')}
          sorter
          sortOrder={getSortOrder(sorters, 'rating')}
          render={(value: number) => (
            <Rate allowHalf={true} disabled value={value} />
          )}
        />

        <Table.Column
          dataIndex="is_accepted"
          title={t('feedback.reviews.fields.status')}
          render={(_, record: ReviewOut) => <ReviewStatusTag record={record} />}
          filtered={
            isFieldFiltered(filters as CrudFilter[], 'accepted_at') ||
            isFieldFiltered(filters as CrudFilter[], 'rejected_at')
          }
          filterDropdown={({ confirm, clearFilters }) => (
            <ReviewStatusFilter
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="reported_at"
          title={t('feedback.reviews.fields.reported_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'reported_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <NullFilter
              field="reported_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => formatDateTime(value)}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => formatDateTime(value)}
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: ReviewOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
